import { LitElement, html, css } from 'lit';
import { api } from '../api.js';
import { EXTRACTION_PROFILES } from '@mv/models';
import { confirmDialog } from '../utils/confirm-dialog.js';
import './instructions-editor.js';
import './scan-sounds-card.js';

/**
 * Company page "Recorder app" (/settings/recorder): everything the company sets for the field
 * recorder, in two sections.
 *  - Instructies: the text the field recorder shows while recording. Starts on the system
 *    default until the company saves its own text. "Reset standaardtekst" reverts to the
 *    system default and is disabled while the company is already on it.
 *  - Scangeluiden: see scan-sounds-card.js.
 * Editable by owner/manager (or sysadmin); others only see the scan sounds.
 */
export class RecorderSettings extends LitElement {
  static properties = {
    _loading: { state: true },
    _saving: { state: true },
    _resetting: { state: true },
    _saved: { state: true },
    _canEdit: { state: true },
    _isDefault: { state: true },
    _error: { state: true },
    _value: { state: true },
    _over: { state: true },
    _profile: { state: true },
    _ready: { state: true },
    _tab: { state: true },        // 'instructions' | 'sounds'
  };

  constructor() {
    super();
    this._profile = 'nen6693';
    this._tab = 'instructions';
    this._loading = true;
    this._saving = false;
    this._resetting = false;
    this._saved = false;
    this._canEdit = false;
    this._isDefault = true;
    this._error = null;
    this._value = '';
    this._html = '';
    this._over = false;
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  async _load() {
    this._loading = true;
    try {
      const res = await api.getRecordingInstructions(this._profile);
      this._value = res?.instructions ?? '';
      this._html = this._value;
      this._isDefault = !!res?.isDefault;
      this._canEdit = !!res?.canEdit;
    } catch (e) {
      this._error = 'Laden mislukt';
    } finally {
      this._loading = false;
      this._ready = true;
    }
  }

  async _switchProfile(id) {
    if (id === this._profile) return;
    this._profile = id;
    this._saved = false; this._error = null; this._over = false;
    await this._load();
  }

  _onChange(e) { this._html = e.detail.html; this._over = e.detail.over; this._saved = false; }

  async _save() {
    this._saving = true; this._saved = false; this._error = null;
    try {
      await api.updateRecordingInstructions(this._profile, this._html);
      this._isDefault = false;
      this._saved = true;
      setTimeout(() => { this._saved = false; }, 2500);
    } catch (e) {
      this._error = 'Opslaan mislukt';
    } finally {
      this._saving = false;
    }
  }

  async _reset() {
    const ok = await confirmDialog({
      title: 'Terug naar standaardtekst',
      message: 'Je eigen instructietekst wordt overschreven door de standaardtekst van het systeem. Wil je doorgaan?',
      confirmLabel: 'Reset',
      danger: true,
    });
    if (!ok) return;
    this._resetting = true; this._error = null;
    try {
      const res = await api.resetRecordingInstructions(this._profile);
      this._value = res?.instructions ?? '';
      this._html = this._value;
      this._isDefault = true;
      this._saved = true;
      setTimeout(() => { this._saved = false; }, 2500);
    } catch (e) {
      this._error = 'Reset mislukt';
    } finally {
      this._resetting = false;
    }
  }

  render() {
    if (!this._ready) return html`<div class="page"><p class="muted">Laden…</p></div>`;
    const tab = (id, label) => html`
      <button class="page-tab ${this._tab === id ? 'active' : ''}" @click=${() => { this._tab = id; }}>${label}</button>`;
    return html`
      <div class="page">
        <h1>Recorder app</h1>
        <p class="subtitle">Wat de veldapp toont en laat horen, voor iedereen in je bedrijf.</p>

        <div class="page-tabs">
          ${tab('instructions', 'Instructies')}
          ${tab('sounds', 'Scangeluiden')}
        </div>

        ${this._tab === 'sounds'
          ? html`<scan-sounds-card .canEdit=${this._canEdit}></scan-sounds-card>`
          : this._renderInstructions()}
      </div>
    `;
  }

  _renderInstructions() {
    if (!this._canEdit) {
      return html`<p class="section-hint">Alleen een beheerder of eigenaar van het bedrijf kan de instructies aanpassen.</p>`;
    }
    return html`
      <p class="section-hint">
        Verschijnt in de veldapp als je tijdens het opnemen op "Instructies" tikt. Elk model heeft
        een eigen tekst; de veldapp toont automatisch die van het model waarmee de opname wordt verwerkt.
      </p>

      <div class="tabs">
        ${EXTRACTION_PROFILES.map((p) => html`
          <button class="tab ${this._profile === p.id ? 'active' : ''}" title=${p.description} @click=${() => this._switchProfile(p.id)}>${p.label}</button>
        `)}
      </div>

      <instructions-editor .value=${this._value} @change=${this._onChange}>
        <span slot="footer" class="badge ${this._isDefault ? 'default' : 'custom'}"
          title=${this._isDefault ? 'Dit model gebruikt de standaardtekst van het systeem.' : 'Je bedrijf heeft voor dit model een eigen tekst.'}>
          ${this._isDefault ? 'Standaardtekst' : 'Aangepast'}
        </span>
        <span slot="footer" class="spacer"></span>
        ${this._error ? html`<span slot="footer" class="err">${this._error}</span>` : ''}
        ${this._saved ? html`<span slot="footer" class="ok">Opgeslagen ✓</span>` : ''}
        <button slot="footer" class="reset" ?disabled=${this._isDefault || this._resetting} @click=${this._reset}>
          ${this._resetting ? 'Resetten…' : 'Reset standaardtekst'}
        </button>
        <button slot="footer" class="save" ?disabled=${this._saving || this._over} @click=${this._save}>${this._saving ? 'Opslaan…' : 'Opslaan'}</button>
      </instructions-editor>
    `;
  }

  static styles = css`
    :host { display: block; }
    .page { max-width: 760px; }
    h1 { margin: 0 0 0.3rem; font-size: 1.5rem; font-weight: 700; color: var(--mv-text); }
    .subtitle { color: var(--mv-text-muted); margin: 0 0 1.25rem; font-size: 0.9rem; }
    .muted { color: var(--mv-text-muted); }

    /* Paginatabs: onderstreept. */
    .page-tabs { display: flex; gap: 22px; border-bottom: 1px solid var(--mv-border); margin-bottom: 1.25rem; }
    .page-tab {
      display: inline-flex; align-items: center; background: none; border: none; cursor: pointer;
      padding: 0 0 10px; margin-bottom: -1px; border-bottom: 2px solid transparent;
      font: inherit; font-size: 0.9rem; font-weight: 600; color: var(--mv-text-muted);
    }
    .page-tab:hover { color: var(--mv-text); }
    .page-tab.active { color: var(--mv-text); border-bottom-color: var(--mv-primary); }

    .section-hint { color: var(--mv-text-muted); margin: 0 0 1rem; font-size: 0.85rem; line-height: 1.5; max-width: 600px; }

    /* Modelkeuze: segmenten. */
    .tabs { display: inline-flex; flex-wrap: wrap; gap: 4px; background: var(--mv-surface-alt, #F3F4F6); padding: 4px; border-radius: 10px; margin-bottom: 1rem; }
    .tab {
      background: transparent; border: none; border-radius: 7px; padding: 6px 14px;
      font: inherit; font-weight: 600; font-size: 0.8rem; cursor: pointer; color: var(--mv-text-sub, #6c757d);
    }
    .tab.active { background: var(--mv-surface, #fff); color: var(--mv-primary); box-shadow: 0 1px 2px rgba(0,0,0,0.06); }

    /* Onderbalk van de editor. */
    .badge { font-size: 0.72rem; font-weight: 600; border-radius: 6px; padding: 3px 8px; }
    .badge.custom { color: var(--mv-primary); background: color-mix(in srgb, var(--mv-primary) 12%, transparent); }
    .badge.default { color: var(--mv-text-muted); background: var(--mv-border); }
    .spacer { flex: 1; }
    .ok { color: var(--mv-primary); font-weight: 600; font-size: 0.85rem; }
    .err { color: #DC2626; font-weight: 600; font-size: 0.85rem; }
    button.save {
      background: var(--mv-primary); color: #fff; border: none; border-radius: 8px;
      padding: 9px 20px; font: inherit; font-weight: 700; font-size: 0.85rem; cursor: pointer;
    }
    button.reset {
      background: var(--mv-surface, #fff); color: var(--mv-text); border: 1px solid var(--mv-border);
      border-radius: 8px; padding: 9px 16px; font: inherit; font-weight: 600; font-size: 0.85rem; cursor: pointer;
    }
    button.reset:hover:not(:disabled) { border-color: #DC2626; color: #DC2626; }
    button:disabled { opacity: 0.55; cursor: default; }
  `;
}

customElements.define('recorder-settings', RecorderSettings);
